import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateTeamIndex } from '../../../../../../utils/firebase/firebase.utils';
import { setTeamIndex } from '../../../../../../store/user/user.action';
import { selectTeamIndex, selectUser } from '../../../../../../store/user/user.selector';

// styles
import {
    TeamSelectionContainer,
    TeamBar,
    ExpandedContent,
    TeamDisplay,
    ButtonsContainer,
    MinimizedContent,
    RightContent,
    TeamDisplayLarge
} from './TeamSelection.styles';

// data
import { TEAM_LOGOS } from '../../../../../../data/theme-data/team-data';
import { GRADIENT_DATA } from '../../../../../../data/theme-data/gradient-data';

// components
import PrimaryBtn from '../../../../../../components/btns/primary-btn/PrimaryBtn';

export default function TeamSelection() {
    const dispatch = useDispatch();
    const teamIndex = useSelector(selectTeamIndex);  // Redux state for current team
    const { uid } = useSelector(selectUser);  // User ID from Redux

    const [isChoosingNewTeam, setIsChoosingNewTeam] = useState(false); // Controls Expanded/Minimized views
    const [activeTeamIndex, setActiveTeamIndex] = useState(teamIndex);  // Tracks the active/current teamIndex
    const [choseNewTeam, setChoseNewTeam] = useState(null);  // Track the newly selected team
    const [previewTeamIndex, setPreviewTeamIndex] = useState(teamIndex);  // Preview team for background changes

    // Change team display logic
    const changeTeamDisplay = (selectedTeamIndex) => {
        if (selectedTeamIndex !== activeTeamIndex) {
            setChoseNewTeam(selectedTeamIndex);  // Only set a new team if it's different from the current team
            setPreviewTeamIndex(selectedTeamIndex);  // Update preview for background
            dispatch(setTeamIndex(selectedTeamIndex));  // Update Redux for immediate UI changes
        } else {
            setChoseNewTeam(null);  // Reset if the user clicks the already active team
            setPreviewTeamIndex(activeTeamIndex);  // Reset preview to active team
            dispatch(setTeamIndex(activeTeamIndex));  // Reset Redux to active team
        }
    }

    // Effect to monitor changes to choseNewTeam and activeTeamIndex
    useEffect(() => {
    }, [choseNewTeam, activeTeamIndex]);

    // Handle team change confirmation
    const handleTeamChange = async () => {
        if (isChoosingNewTeam) {
            if (choseNewTeam !== null && choseNewTeam !== activeTeamIndex) {
                // Update Firestore and local state
                await updateTeamIndex(uid, choseNewTeam, activeTeamIndex);
                setActiveTeamIndex(choseNewTeam);  // Set the newly chosen team a active
                setPreviewTeamIndex(choseNewTeam);  // Update preview to match active team
                dispatch(setTeamIndex(choseNewTeam));  // Update Redux state
            }
            setIsChoosingNewTeam(false);  // Close the selection UI
            setChoseNewTeam(null);  // Reset the selected team
        } else {
            setIsChoosingNewTeam(true);  // Open the selection UI
        }
    }

    // Handle canceling team change
    const handleCancelTeamChange = () => {
        setIsChoosingNewTeam(false);  // Close the selection UI
        setChoseNewTeam(null);  // Reset the selected team
        setPreviewTeamIndex(activeTeamIndex);  // Reset preview to active team
        dispatch(setTeamIndex(activeTeamIndex));  // Restore the previously active team
    }

    // Get background style based on preview team
    const getBackgroundStyle = () => {
        const teamColors = GRADIENT_DATA[previewTeamIndex];
        if (teamColors) {
            return {
                background: `linear-gradient(
                    135deg,
                    rgba(${teamColors[0]}, 0.4) 0%,
                    rgba(${teamColors[1]}, 0.4) 100%
                  )`,
            };
        }
        return {};
    }

    return (
        <TeamSelectionContainer>
            <TeamBar className='panel-bg' style={getBackgroundStyle()}>
                {isChoosingNewTeam ? (
                    <ExpandedContent>
                        {Object.keys(TEAM_LOGOS).map((key, index) => (
                            <TeamDisplay
                                key={index}
                                onClick={() => changeTeamDisplay(index)}
                                className={index === (choseNewTeam ?? activeTeamIndex) ? 'active' : ''}
                            >
                                <img
                                    src={TEAM_LOGOS[key].logo}
                                    alt={TEAM_LOGOS[key].name}
                                    className={index === (choseNewTeam ?? activeTeamIndex) ? 'active' : ''}
                                />
                            </TeamDisplay>
                        ))}
                    </ExpandedContent>
                ) : (
                    <MinimizedContent>
                        <img
                            className="circle-img-left"
                            src={TEAM_LOGOS[previewTeamIndex].logo}
                            alt={TEAM_LOGOS[previewTeamIndex].name}
                        />
                        <h3>{TEAM_LOGOS[previewTeamIndex].name}</h3>
                    </MinimizedContent>
                )}
            </TeamBar>

            <RightContent>
                <ButtonsContainer>
                    <div onClick={handleTeamChange}>
                        <PrimaryBtn
                            disabled={isChoosingNewTeam && (choseNewTeam === null || choseNewTeam === activeTeamIndex)}
                            title={isChoosingNewTeam ? 'SAVE' : 'CHANGE TEAM'}
                        />
                    </div>
                    {isChoosingNewTeam &&
                        <PrimaryBtn title="CANCEL" onClick={handleCancelTeamChange} />
                    }
                </ButtonsContainer>
                {isChoosingNewTeam &&
                    <TeamDisplayLarge className='panel-bg' style={getBackgroundStyle()}>
                        <img src={TEAM_LOGOS[previewTeamIndex].logo} alt={TEAM_LOGOS[previewTeamIndex].name} />
                        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '2px' }}>
                            <h3>{TEAM_LOGOS[previewTeamIndex].locationName}</h3>
                            <h3>{TEAM_LOGOS[previewTeamIndex].teamName}</h3>
                        </div>
                    </TeamDisplayLarge>
                }
            </RightContent>
        </TeamSelectionContainer>
    );
}